import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Calendar,
  GraduationCap,
  School,
  ClipboardList
} from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const LearnMorePage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F9F7FE] to-[#EADFFF] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Complete Guide to <br />
              <span className="text-[#7B4AE2]">11th Grade Admissions</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Everything you need to know about the 11th standard admission process for 2025-26
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
            >
              Start Your Application
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Important Dates */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 flex items-center">
              <Calendar className="h-6 w-6 text-[#7B4AE2] mr-2" />
              Important Dates & Schedule
            </h2>
            
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-24 font-medium text-[#7B4AE2]">Part 1</div>
                  <div>
                    <h3 className="font-medium mb-2">Student Registration</h3>
                    <p className="text-gray-600 text-sm">
                      Online registration and form filling for all students
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-24 font-medium text-[#7B4AE2]">Part 2</div>
                  <div>
                    <h3 className="font-medium mb-2">Option Form Filling</h3>
                    <p className="text-gray-600 text-sm">
                      Selection of preferred colleges and streams
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-24 font-medium text-[#7B4AE2]">Part 3</div>
                  <div>
                    <h3 className="font-medium mb-2">Merit List & Admissions</h3>
                    <p className="text-gray-600 text-sm">
                      Publication of merit lists and admission process
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Important Note</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    All dates are tentative and subject to change. Stay updated by regularly checking the official website or using our platform.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Process */}
      <section className="bg-[#F9F7FE] py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 flex items-center">
              <FileText className="h-6 w-6 text-[#7B4AE2] mr-2" />
              Registration Process
            </h2>

            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <h3 className="text-lg font-semibold mb-4">Required Documents</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">For Registration</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      SSC Marksheet
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      School Leaving Certificate
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Caste Certificate (if applicable)
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">For Final Admission</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Original Documents
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Document Verification
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      Fee Payment
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Step-by-Step Process</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium mb-2 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-[#F0E9FF] text-[#7B4AE2] flex items-center justify-center text-sm mr-2">1</span>
                    Online Registration
                  </h4>
                  <p className="text-sm text-gray-600 ml-8">
                    Fill in your personal details and academic information accurately
                  </p>
                </div>

                <div>
                  <h4 className="font-medium mb-2 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-[#F0E9FF] text-[#7B4AE2] flex items-center justify-center text-sm mr-2">2</span>
                    Document Upload
                  </h4>
                  <p className="text-sm text-gray-600 ml-8">
                    Upload scanned copies of all required documents
                  </p>
                </div>

                <div>
                  <h4 className="font-medium mb-2 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-[#F0E9FF] text-[#7B4AE2] flex items-center justify-center text-sm mr-2">3</span>
                    College Selection
                  </h4>
                  <p className="text-sm text-gray-600 ml-8">
                    Choose your preferred colleges and streams in order of preference
                  </p>
                </div>

                <div>
                  <h4 className="font-medium mb-2 flex items-center">
                    <span className="w-6 h-6 rounded-full bg-[#F0E9FF] text-[#7B4AE2] flex items-center justify-center text-sm mr-2">4</span>
                    Merit List
                  </h4>
                  <p className="text-sm text-gray-600 ml-8">
                    Check merit lists and confirm admission if selected
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Information */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 flex items-center">
              <School className="h-6 w-6 text-[#7B4AE2] mr-2" />
              Additional Information
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4">Reservation Policy</h3>
                <ul className="space-y-2">
                  <li className="text-sm text-gray-600">
                    • SC/ST Category: As per government norms
                  </li>
                  <li className="text-sm text-gray-600">
                    • OBC Category: Subject to valid certificate
                  </li>
                  <li className="text-sm text-gray-600">
                    • Special Categories: Sports, Cultural quotas
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4">Fee Structure</h3>
                <ul className="space-y-2">
                  <li className="text-sm text-gray-600">
                    • Registration Fee: As applicable
                  </li>
                  <li className="text-sm text-gray-600">
                    • Tuition Fee: Varies by college
                  </li>
                  <li className="text-sm text-gray-600">
                    • Development Fee: As per institution
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Help & Support */}
      <section className="bg-[#F9F7FE] py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 flex items-center">
              <ClipboardList className="h-6 w-6 text-[#7B4AE2] mr-2" />
              Help & Support
            </h2>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4">How We Can Help</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 mr-2" />
                      <span className="text-sm text-gray-600">
                        AI-powered college recommendations based on your profile
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 mr-2" />
                      <span className="text-sm text-gray-600">
                        Real-time cutoff predictions and analysis
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-1 mr-2" />
                      <span className="text-sm text-gray-600">
                        Document verification and processing assistance
                      </span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Get Started</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Create your account now to get personalized college recommendations and admission guidance.
                  </p>
                  <Link 
                    to="/register" 
                    className="inline-flex items-center px-4 py-2 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors text-sm"
                  >
                    Create Account
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-[#7B4AE2] to-[#5A35A7] rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Start Your Admission Journey?
            </h2>
            <p className="text-white text-opacity-90 mb-8 max-w-2xl mx-auto">
              Get personalized college recommendations and expert guidance throughout your admission process
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center px-8 py-4 bg-white text-[#7B4AE2] rounded-lg hover:bg-opacity-90 transition-colors font-medium"
            >
              Get Started Free
              <GraduationCap className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LearnMorePage;